import React, { useState } from 'react';
import { App, Button, InputNumber, Spin } from 'antd';
import { EditOutlined, ReloadOutlined, TeamOutlined, UndoOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import dynamicFormsService from '../../services/dynamicFormsService';

const NAME_KEYS = ['full_name', 'name'];

const bookerName = (b) => {
  const info = b.submitter_info || {};
  const name = NAME_KEYS.map((k) => info[k]).find(Boolean) || [info.first_name, info.last_name].filter(Boolean).join(' ');
  return name || info.email || `Response #${b.submission_id}`;
};

const Status = ({ w }) => {
  if (!w.capacity_set) return <span className="dfw-pill">No limit set</span>;
  if (w.is_full) return <span className="dfw-pill is-bad">Full</span>;
  if (w.available <= 3) return <span className="dfw-pill is-warn">{w.available} left</span>;
  return <span className="dfw-pill is-ok">{w.available} left</span>;
};

/** One option: places booked, change the limit, reset the count, and who booked. */
const OptionRow = ({ formId, w, liveIds, onChange }) => {
  const { message, modal } = App.useApp();
  const [editing, setEditing] = useState(false);
  const [max, setMax] = useState(w.max_pax);
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [open, setOpen] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const saveMax = async () => {
    if (!max || max < w.current_pax) {
      message.error(`Places can't be fewer than the ${w.current_pax} already booked`);
      return;
    }
    try {
      setSaving(true);
      const response = await dynamicFormsService.updateWorkshopCapacity(formId, w.workshop_id, max);
      if (!response.success) throw new Error(response.error);
      message.success('Places updated');
      setEditing(false);
      onChange();
    } catch (err) {
      message.error(err.message || 'Could not update the places');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    modal.confirm({
      title: `Reset the count for “${w.option_label}”?`,
      content: `The ${w.current_pax} booked ${w.current_pax === 1 ? 'place goes' : 'places go'} back to 0, so the option reopens. Responses are not deleted.`,
      okText: 'Reset count',
      okButtonProps: { danger: true },
      rootClassName: 'do-scope',
      onOk: async () => {
        try {
          const response = await dynamicFormsService.resetWorkshopPax(formId, w.workshop_id);
          if (!response.success) throw new Error(response.error);
          message.success('Count reset');
          onChange();
        } catch (err) {
          message.error(err.message || 'Could not reset the count');
        }
      },
    });
  };

  const toggleBookings = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    try {
      setLoadingBookings(true);
      const response = await dynamicFormsService.getWorkshopBookings(formId, w.workshop_id);
      if (!response.success) throw new Error(response.error);
      // bookings of deleted responses are left out, as before
      setBookings((response.bookings || []).filter((b) => !liveIds || liveIds.has(b.submission_id)));
    } catch (err) {
      message.error(err.message || 'Could not load the bookings');
      setOpen(false);
    } finally {
      setLoadingBookings(false);
    }
  };

  return (
    <li className={`dfw-place${w.is_full ? ' is-full' : ''}`}>
      <div className="dfw-place__main">
        <span className="dfw-place__name">{w.option_label}</span>
        <span className="dfw-place__count">
          {w.capacity_set ? (
            <><strong className="dfw-num">{w.current_pax}</strong> of <span className="dfw-num">{w.max_pax}</span> booked</>
          ) : (
            'Set places by editing the question'
          )}
        </span>
        <Status w={w} />
        {w.capacity_set && (
          <span className="dfw-place__actions">
            <Button size="small" type="text" icon={open ? <UpOutlined aria-hidden="true" /> : <TeamOutlined aria-hidden="true" />} onClick={toggleBookings} aria-expanded={open}>
              {open ? 'Hide' : 'Bookings'}
            </Button>
            <Button size="small" type="text" icon={<EditOutlined aria-hidden="true" />} onClick={() => { setMax(w.max_pax); setEditing((v) => !v); }} aria-expanded={editing}>
              Places
            </Button>
            <Button size="small" type="text" danger icon={<UndoOutlined aria-hidden="true" />} onClick={reset} disabled={w.current_pax === 0}>
              Reset
            </Button>
          </span>
        )}
      </div>

      {editing && (
        <div className="dfw-place__edit">
          <label htmlFor={`dfw-max-${w.workshop_id}`}>Places for “{w.option_label}”</label>
          <InputNumber id={`dfw-max-${w.workshop_id}`} min={Math.max(1, w.current_pax)} max={10000} value={max} onChange={setMax} onPressEnter={saveMax} />
          <span className="dfw-hint">At least {Math.max(1, w.current_pax)}, the places already booked.</span>
          <span className="dfw-grow" />
          <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
          <Button size="small" type="primary" onClick={saveMax} loading={saving}>Save</Button>
        </div>
      )}

      {open && (
        <div className="dfw-place__bookings">
          {loadingBookings ? (
            <Spin size="small" />
          ) : !bookings || bookings.length === 0 ? (
            <p className="dfw-hint dfw-hint--flush">No bookings yet.</p>
          ) : (
            <ol>
              {bookings.map((b) => (
                <li key={b.booking_id}>
                  <span className="dfw-place__who">{bookerName(b)}</span>
                  {b.submitter_info?.email && <span className="dfw-place__mail">{b.submitter_info.email}</span>}
                  {b.pax_count > 1 && <span className="dfw-place__pax">{b.pax_count} places</span>}
                  <span className="dfw-place__when">{dayjs(b.submitted_at).format('D MMM, HH:mm')}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Places per option for every question with limited places, from GET /forms/:id/workshops.
 * Full options are closed on the public form automatically.
 */
const WorkshopPlaces = ({ formId, workshopFields, loading, onReload, liveIds }) => (
  <div className="dfw-places">
    <div className="dfw-builder__bar">
      <p className="dfw-builder__lede">Full options close on the form automatically. Deleting a response frees its places.</p>
      <span className="dfw-grow" />
      <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={onReload} loading={loading}>Refresh</Button>
    </div>
    {loading && !workshopFields ? (
      <div className="dfw-empty"><Spin /></div>
    ) : !workshopFields || workshopFields.length === 0 ? (
      <div className="dfw-empty">
        <strong>No questions with limited places</strong>
        <span>Turn on “Limit places” for a dropdown, radio or checkbox question to manage places here.</span>
      </div>
    ) : (
      workshopFields.map((f) => {
        const full = f.workshops.filter((w) => w.is_full).length;
        return (
          <section className="dfw-group" key={f.field_id} aria-labelledby={`dfw-w-${f.field_id}`}>
            <h3 className="dfw-group__title" id={`dfw-w-${f.field_id}`}>
              {f.display_label || f.field_name}
              <span className="dfw-group__meta">
                {f.workshops.length} {f.workshops.length === 1 ? 'option' : 'options'}{full > 0 ? ` · ${full} full` : ''}
              </span>
            </h3>
            <ul className="dfw-place-list">
              {f.workshops.map((w) => (
                <OptionRow key={`${w.workshop_id ?? 'x'}-${w.option_label}`} formId={formId} w={w} liveIds={liveIds} onChange={onReload} />
              ))}
            </ul>
          </section>
        );
      })
    )}
  </div>
);

export default WorkshopPlaces;
